<template>
  <div class="min-h-screen" data-theme="yellow">
    <NavBar :signed-in="signedIn" :user-name="userName" active-page="forum" />
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <a :href="'/forum'" class="text-sm text-blue-700 hover:text-blue-900 mb-4 inline-block">&larr; {{ t('forum.back_to_forum') }}</a>

      <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <h1 class="text-xl font-bold text-gray-900">{{ topic.title }}</h1>
        <p class="text-xs text-gray-500 mt-1">{{ topic.user.name }} · {{ formatDate(topic.created_at) }}</p>
      </div>

      <!-- Messages -->
      <div class="space-y-4 mb-6">
        <div v-for="msg in messages" :key="msg.id" class="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div class="flex items-start gap-3">
            <span class="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-sm font-medium text-blue-800 shrink-0 mt-0.5">
              {{ msg.user.name.charAt(0).toUpperCase() }}
            </span>
            <div class="flex-1">
              <div class="flex items-center gap-2 mb-1">
                <span v-if="signedIn && msg.user.id !== userId" @click="openChatWith(msg.user)" class="text-sm font-medium text-gray-900 hover:text-blue-700 cursor-pointer">{{ msg.user.name }}</span>
                <span v-else class="text-sm font-medium text-gray-900">{{ msg.user.name }}</span>
                <span class="text-xs text-gray-400">{{ formatDateTime(msg.created_at) }}</span>
                <span v-if="msg.updated_at !== msg.created_at" class="text-xs text-gray-400 italic">{{ t('forum.edited') }}</span>
              </div>

              <!-- Reply reference -->
              <div v-if="msg.parent_preview" class="mb-1 px-3 py-1.5 bg-gray-50 border-l-2 border-blue-300 rounded text-xs text-gray-500">
                <span class="font-medium text-gray-700">{{ msg.parent_preview.user_name }}</span>: {{ msg.parent_preview.body }}
              </div>

              <!-- Edit mode -->
              <div v-if="editingId === msg.id" class="flex gap-2">
                <input v-model="editBody" type="text" class="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                <button @click="saveEdit(msg)" class="px-3 py-1.5 text-xs font-medium text-white bg-blue-700 rounded-lg hover:bg-blue-800">{{ t('forum.save') }}</button>
                <button @click="editingId = null" class="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">{{ t('forum.cancel') }}</button>
              </div>
              <!-- Display mode -->
              <p v-else class="text-sm text-gray-700 whitespace-pre-line">{{ msg.body }}</p>

              <!-- Actions -->
              <div v-if="!editingId" class="mt-2 flex gap-2">
                <button v-if="signedIn" @click="startReply(msg)" class="text-xs text-blue-600 hover:text-blue-800">{{ t('forum.reply') }}</button>
                <button v-if="msg.user.id === userId || userAdmin" @click="startEdit(msg)" class="text-xs text-blue-600 hover:text-blue-800">{{ t('forum.edit') }}</button>
                <button v-if="msg.user.id === userId || userAdmin" @click="deleteMessage(msg)" class="text-xs text-red-400 hover:text-red-600">{{ t('forum.delete') }}</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <p v-if="messages.length === 0" class="text-center text-gray-400 text-sm mb-6">{{ t('forum.no_messages') }}</p>

      <!-- New message -->
      <div v-if="signedIn" class="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div v-if="replyTo" class="mb-2 px-3 py-1.5 bg-gray-50 border-l-2 border-blue-300 rounded text-xs text-gray-500 flex items-center justify-between">
          <span><span class="font-medium text-gray-700">{{ replyTo.user.name }}</span>: {{ replyTo.body.substring(0, 80) }}</span>
          <button @click="replyTo = null" class="text-gray-400 hover:text-gray-600 ml-2">&times;</button>
        </div>
        <form @submit.prevent="sendMessage">
          <div class="flex gap-2 items-center mb-2">
            <input ref="messageInput" v-model="newMessage" type="text" :placeholder="replyTo ? t('forum.reply_placeholder') : t('forum.message_placeholder')" required
                   class="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <div class="relative">
              <button type="button" @click="showEmojis = !showEmojis" class="text-gray-400 hover:text-gray-600 text-xl p-1">😊</button>
              <div v-if="showEmojis" class="absolute bottom-full right-0 mb-1 bg-white border border-gray-200 rounded-lg shadow-lg p-2 z-50 w-64 flex flex-wrap gap-1">
                <button v-for="e in emojis" :key="e" type="button" @click="addEmoji(e)" class="text-xl hover:bg-gray-100 rounded p-1 cursor-pointer">{{ e }}</button>
              </div>
            </div>
            <button type="submit" :disabled="!newMessage.trim()"
                    class="px-4 py-2 text-sm font-medium text-white bg-blue-700 rounded-lg hover:bg-blue-800 transition disabled:opacity-50">
              {{ t('forum.send') }}
            </button>
          </div>
        </form>
      </div>
      <p v-else class="text-center text-sm text-gray-500">
        <a href="/users/sign_in" class="text-blue-700 hover:underline">{{ t('auth.sign_in') }}</a>
        {{ t('forum.to_write') }}
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import NavBar from './NavBar.vue'

const props = defineProps({ signedIn: Boolean, userName: String, userId: Number, userAdmin: Boolean, topic: Object, messages: Array, csrfToken: String })
const { t, locale } = useI18n()

const showMenu = ref(false)
const logoSize = ref(120)
const msgs = ref([...props.messages])
const newMessage = ref('')
const editingId = ref(null)
const editBody = ref('')
const replyTo = ref(null)
const messageInput = ref(null)
const showEmojis = ref(false)
const emojis = ['😊', '😂', '🤣', '😍', '🥰', '😎', '🤔', '😢', '😡', '👍', '👎', '👏', '🙌', '💪', '🏃', '🏃‍♀️', '🏅', '🥇', '🥈', '🥉', '🎉', '🔥', '❤️', '💯', '⭐', '🌟', '☀️', '🌧️', '💨', '🗺️', '📍', '⏱️', '🎯', '✅', '❌', '⚡']

function addEmoji(e) {
  newMessage.value += e
  showEmojis.value = false
  if (messageInput.value) messageInput.value.focus()
}

function openChatWith(user) {
  window.dispatchEvent(new CustomEvent('sidebar:open-chat', { detail: { id: user.id, name: user.name } }))
}

function formatDate(d) { return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) }
function formatDateTime(d) { const dt = new Date(d); return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) + ' ' + dt.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) }

async function request(url, method, body = null) {
  const o = { method, headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': props.csrfToken, 'Accept': 'application/json' } }
  if (body) o.body = JSON.stringify(body)
  return fetch(url, o)
}

function startReply(msg) {
  replyTo.value = msg
  if (messageInput.value) messageInput.value.focus()
}

async function sendMessage() {
  if (!newMessage.value.trim()) return
  const payload = { message: { body: newMessage.value } }
  if (replyTo.value) payload.message.parent_id = replyTo.value.id
  try {
    const res = await request(`/forum/${props.topic.id}/messages`, 'POST', payload)
    if (res.ok) { msgs.value.push(await res.json()); newMessage.value = ''; replyTo.value = null }
  } catch {}
}

function startEdit(msg) { editingId.value = msg.id; editBody.value = msg.body }

async function saveEdit(msg) {
  try {
    const res = await request(`/forum/${props.topic.id}/messages/${msg.id}`, 'PATCH', { message: { body: editBody.value } })
    if (res.ok) {
      const data = await res.json()
      const idx = msgs.value.findIndex(m => m.id === msg.id)
      if (idx !== -1) msgs.value[idx] = data
      editingId.value = null
    }
  } catch {}
}

async function deleteMessage(msg) {
  if (!confirm(t('forum.confirm_delete_message'))) return
  try {
    const res = await request(`/forum/${props.topic.id}/messages/${msg.id}`, 'DELETE')
    if (res.ok) msgs.value = msgs.value.filter(m => m.id !== msg.id)
  } catch {}
}

// Use msgs instead of props.messages in template
const messages = msgs
</script>
